import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DollarSign, Package, TrendingUp, ShoppingCart, ArrowUpRight, Clock, CheckCircle, Crown, CreditCard, ExternalLink, AlertCircle, Loader2, Edit3, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function CreatorDashboardPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [payingOut, setPayingOut] = useState(false);
  const [tab, setTab] = useState('overview');

  useEffect(() => { 
    loadData();
    // Check for Stripe Connect return
    const connectParam = searchParams.get('connect');
    if (connectParam === 'complete') {
      toast.success('Stripe Connect setup updated! Checking status...');
      loadConnectStatus();
    } else if (connectParam === 'refresh') {
      toast.info('Please complete Stripe Connect onboarding');
      startOnboarding();
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [profileRes, productsRes, txnRes, payoutRes, connectRes] = await Promise.all([
        api.get('/creator/dashboard').catch(() => ({ data: null })),
        api.get('/creator/products').catch(() => ({ data: [] })),
        api.get('/creator/transactions').catch(() => ({ data: [] })),
        api.get('/creator/payouts').catch(() => ({ data: [] })),
        api.get('/creator/connect/status').catch(() => ({ data: null }))
      ]);
      setStats(profileRes.data);
      setProducts(productsRes.data || []);
      setTransactions(txnRes.data || []);
      setPayouts(payoutRes.data || []);
      setConnectStatus(connectRes.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectStatus = async () => {
    try {
      const res = await api.get('/creator/connect/status');
      setConnectStatus(res.data);
    } catch {}
  };

  const requestPayout = async () => {
    setRequesting(true);
    try {
      await api.post('/creator/payouts/request', {});
      toast.success('Payout requested');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payout request failed');
    } finally {
      setRequesting(false);
    }
  };

  const startOnboarding = async () => {
    setOnboarding(true);
    try {
      const res = await api.post('/creator/connect/onboard');
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start onboarding');
      setOnboarding(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await api.get('/creator/connect/dashboard');
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open dashboard');
    }
  };

  const initiateStripePayout = async () => {
    setPayingOut(true);
    try {
      const res = await api.post('/creator/connect/payout');
      toast.success(`Payout of €${res.data.amount.toFixed(2)} initiated!`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payout failed');
    } finally {
      setPayingOut(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground text-sm">Loading dashboard...</div>;

  const totalEarnings = stats?.total_earnings || 0;
  const totalSales = stats?.total_sales_count || stats?.sales_count || 0;
  const totalRevenue = stats?.total_revenue || 0;
  const totalCommission = stats?.total_commission || 0;
  const pendingBalance = stats?.pending_balance || 0;
  const paidOut = stats?.paid_out || 0;
  const commissionRate = stats?.commission_rate || 0.15;
  const commissionTier = stats?.commission_tier || 'starter_creator';
  const tierInfo = stats?.tier_info || { name: 'Starter Creator', label: 'Starter Creator — 15%' };

  return (
    <div className="animate-in" data-testid="creator-dashboard-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-2xl md:text-3xl">Creator Dashboard</h1>
        <Link to="/inner-circle">
          <Button data-testid="dashboard-inner-circle-btn" className="rounded-sm text-xs bg-gold text-obsidian hover:bg-obsidian hover:text-gold h-9 px-4">
            <Crown size={14} className="mr-2" /> Join Inner Circle
          </Button>
        </Link>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Track sales, earnings, commissions, and payouts</p>

      {/* Subscription Status Card */}
      {user && user.role !== 'admin' && (
        <div className="mb-6 p-5 border border-border/40 rounded-sm bg-gradient-to-r from-amber-50/50 to-rose-50/30" data-testid="subscription-status-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <CreditCard size={18} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Membership</p>
                <p className="font-serif text-lg capitalize">
                  {user.subscription_status === 'trial' ? 'Free Trial' : 
                   user.subscription_status === 'active' ? `${user.subscription_plan || ''} Plan` : 
                   'Expired'}
                </p>
              </div>
            </div>
            <div className="text-right">
              {user.subscription_status === 'trial' && user.trial_end_date && (
                <div>
                  <p className="text-xs text-muted-foreground">Trial ends</p>
                  <p className="text-sm font-medium text-amber-700">
                    {new Date(user.trial_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              )}
              {user.subscription_status === 'active' && user.subscription_end_date && (
                <div>
                  <p className="text-xs text-muted-foreground">Renews</p>
                  <p className="text-sm font-medium text-emerald-700">
                    {new Date(user.subscription_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              )}
              {user.subscription_status === 'expired' && (
                <Link to="/pricing">
                  <Button className="rounded-full text-xs bg-amber-500 hover:bg-amber-600 h-8 px-4">
                    Subscribe Now
                  </Button>
                </Link>
              )}
              {user.subscription_status === 'trial' && (
                <Link to="/pricing">
                  <Button variant="outline" className="rounded-full text-xs border-amber-300 text-amber-700 hover:bg-amber-50 h-8 px-4 mt-1">
                    View Plans
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Commission Tier Banner */}
      <div className="mb-8 p-4 border border-gold/30 bg-gradient-to-r from-gold/10 to-purple-500/10 rounded-sm" data-testid="commission-tier-banner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center">
              <Crown size={18} className="text-obsidian" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Your Commission Tier</p>
              <p className="font-serif text-lg">{tierInfo.label || tierInfo.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Platform Commission</p>
            <p className="text-2xl font-bold text-gold">{(commissionRate * 100).toFixed(0)}%</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="border border-border/40 p-5" data-testid="stat-total-earnings">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gold/10 flex items-center justify-center"><DollarSign size={16} className="text-gold" /></div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Your Earnings</span>
          </div>
          <p className="font-serif text-2xl text-gold">€{totalEarnings.toFixed(2)}</p>
          <p className="text-[9px] text-muted-foreground mt-1">After commission</p>
        </div>
        <div className="border border-border/40 p-5" data-testid="stat-total-sales">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-obsidian/5 flex items-center justify-center"><ShoppingCart size={16} /></div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Sales</span>
          </div>
          <p className="font-serif text-2xl">{totalSales}</p>
          <p className="text-[9px] text-muted-foreground mt-1">Revenue: €{totalRevenue.toFixed(2)}</p>
        </div>
        <div className="border border-border/40 p-5" data-testid="stat-pending-balance">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-obsidian/5 flex items-center justify-center"><TrendingUp size={16} /></div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending Balance</span>
          </div>
          <p className="font-serif text-2xl">€{pendingBalance.toFixed(2)}</p>
          {pendingBalance > 0 && (
            <Button data-testid="request-payout-btn" size="sm" variant="outline" className="mt-2 text-[10px] h-7 rounded-sm" onClick={requestPayout} disabled={requesting}>
              <ArrowUpRight size={10} className="mr-1" /> {requesting ? 'Requesting...' : 'Request Payout'}
            </Button>
          )}
        </div>
        <div className="border border-border/40 p-5" data-testid="stat-commission-paid">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-obsidian/5 flex items-center justify-center"><Package size={16} /></div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Commission Paid</span>
          </div>
          <p className="font-serif text-2xl">€{totalCommission.toFixed(2)}</p>
          <p className="text-[9px] text-muted-foreground mt-1">Paid out: €{paidOut.toFixed(2)}</p>
        </div>
      </div>

      {/* Payout Breakdown Card */}
      <div className="mb-8 p-5 border border-border/40 bg-mist/30" data-testid="payout-breakdown">
        <h3 className="font-serif text-base mb-4">Payout Breakdown</h3>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div className="p-3 bg-white rounded border border-border/20">
            <p className="text-xs text-muted-foreground mb-1">Customer Pays</p>
            <p className="font-semibold">€{totalRevenue.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded border border-border/20">
            <p className="text-xs text-muted-foreground mb-1">Platform Commission ({(commissionRate*100).toFixed(0)}%)</p>
            <p className="font-semibold text-red-500">-€{totalCommission.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded border border-border/20">
            <p className="text-xs text-muted-foreground mb-1">Production Costs</p>
            <p className="font-semibold text-red-500">-€{(totalRevenue - totalEarnings - totalCommission).toFixed(2)}</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
            <p className="text-xs text-emerald-600 mb-1">Your Payout</p>
            <p className="font-bold text-emerald-700">€{totalEarnings.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Stripe Connect - Coming Soon */}
      <Card className="mb-8 rounded-sm border-dashed border-border/50 bg-stone-50/50" data-testid="stripe-connect-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 font-serif text-base text-muted-foreground">
            <CreditCard size={18} className="text-muted-foreground/50" />
            Marketplace Payouts — Coming Soon
          </CardTitle>
          <CardDescription className="text-xs">
            Stripe Connect integration for automatic seller payouts is coming in a future update.
            For now, use your own payment links for direct sales.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-border/30">
        {['overview', 'transactions', 'payouts'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${tab === t ? 'border-b-2 border-obsidian font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="border border-border/40">
          <div className="p-5 border-b border-border/20">
            <h2 className="font-serif text-lg">Your Products</h2>
          </div>
          {products.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-products-message">No products listed yet.</div>
          ) : (
            <div className="divide-y divide-border/20">
              {products.map(p => {
                const imgSrc = p.images?.[0]?.startsWith('data:') ? p.images[0] : p.images?.[0] ? `data:image/jpeg;base64,${p.images[0]}` : null;
                return (
                <div key={p.id} className="flex items-center gap-4 p-4 hover:bg-mist/30 transition-colors" data-testid={`creator-product-${p.id}`}>
                  <div className="w-10 h-10 bg-mist border border-border/20 flex items-center justify-center shrink-0 overflow-hidden rounded">
                    {imgSrc ? (
                      <img src={imgSrc} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package size={16} className="text-muted-foreground/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-[10px] text-muted-foreground">{p.file_count || 0} files · {p.category || p.product_type || 'digital'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-serif text-gold">€{p.price?.toFixed(2)}</p>
                    <p className="text-[10px] text-muted-foreground">{p.purchases || 0} sold</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Link to={`/products/${p.id}/edit`}>
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0" data-testid={`edit-product-${p.id}`}>
                        <Edit3 size={12} />
                      </Button>
                    </Link>
                    <Link to={`/products/${p.id}`}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <Eye size={12} />
                      </Button>
                    </Link>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {tab === 'transactions' && (
        <div className="border border-border/40">
          <div className="p-5 border-b border-border/20">
            <h2 className="font-serif text-lg">Sales History</h2>
          </div>
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No sales yet.</div>
          ) : (
            <div className="divide-y divide-border/20">
              {transactions.map(t => (
                <div key={t.id} className="p-4 flex items-center justify-between" data-testid={`txn-${t.id}`}>
                  <div>
                    <p className="text-sm font-medium">{t.product_title}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-serif text-gold">+{t.seller_earnings?.toFixed(2)}</p>
                    <p className="text-[10px] text-muted-foreground">-{t.commission_amount?.toFixed(2)} commission ({(t.commission_rate * 100).toFixed(0)}%)</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payouts Tab */}
      {tab === 'payouts' && (
        <div className="border border-border/40">
          <div className="p-5 border-b border-border/20">
            <h2 className="font-serif text-lg">Payout History</h2>
          </div>
          {payouts.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No payouts requested yet.</div>
          ) : (
            <div className="divide-y divide-border/20">
              {payouts.map(p => (
                <div key={p.id} className="p-4 flex items-center justify-between" data-testid={`payout-${p.id}`}>
                  <div className="flex items-center gap-3">
                    {p.status === 'completed' ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <Clock size={16} className="text-amber-500" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{p.amount?.toFixed(2)}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${p.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
